import { useState } from "react";
import QRCode from "react-qr-code";
import { motion, AnimatePresence } from "framer-motion";
import { X, QrCode, Copy, Eye, EyeSlash } from "@phosphor-icons/react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

export default function LocationQR({ location, onUpdated, canUseQr = true }) {
  const [open, setOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(!!location.qr_public);
  const url = `${window.location.origin}/p/loc/${location.public_token}`;

  const togglePublic = async () => {
    if (!canUseQr) { toast.error("Upgrade to Pro to share via QR."); return; }
    try {
      const { data } = await api.patch(`/locations/${location.id}/qr`, { qr_public: !isPublic });
      setIsPublic(data.qr_public);
      onUpdated?.(data);
      toast.success(data.qr_public ? "Public viewing enabled" : "Made private");
    } catch (e) { toast.error(formatError(e)); }
  };

  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  const print = () => {
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>${location.name} — Nestled QR</title>
      <style>body{font-family:system-ui;display:flex;flex-direction:column;align-items:center;padding:48px;color:#2C2420;background:#F9F6F0}
      h1{font-weight:600;margin:24px 0 4px}p{color:#5C544F;margin:0 0 24px}
      .card{background:#fff;padding:32px;border-radius:24px;border:1px solid #E6E2D8;text-align:center}</style>
      </head><body><div class="card">
      <div id="qr"></div><h1>${location.name}</h1><p>Scan to view contents</p></div>
      <script src="https://unpkg.com/qrcode-generator@1.4.4/qrcode.js"></script>
      <script>var q=qrcode(0,'M');q.addData(${JSON.stringify(url)});q.make();document.getElementById('qr').innerHTML=q.createImgTag(6,0);<\/script>
      </body></html>`);
    w.document.close();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => canUseQr ? setOpen(true) : toast.error("Upgrade to Pro to use QR labels.")}
        data-testid={`location-qr-${location.id}`}
        className="p-2 rounded-lg hover:bg-sand-200 text-stoneink-700 transition-colors"
        title="QR label"
      >
        <QrCode weight="duotone" size={18} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-stoneink-900/40 backdrop-blur-md grid place-items-center p-4"
            onClick={() => setOpen(false)}
          >
            <motion.div initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl border border-sand-300 shadow-hover p-8 max-w-md w-full"
              data-testid="qr-modal"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-heading text-2xl font-medium text-stoneink-900">{location.name}</h3>
                <button onClick={() => setOpen(false)} className="h-9 w-9 grid place-items-center rounded-xl hover:bg-sand-200"><X size={18} /></button>
              </div>
              <p className="text-sm text-stoneink-500">Stick this label on the box, shelf, or drawer.</p>

              <div className="my-6 flex justify-center">
                <div className="p-4 rounded-2xl border border-sand-300 bg-white">
                  <QRCode value={url} size={196} bgColor="#FFFFFF" fgColor="#2C2420" />
                </div>
              </div>

              <div className="rounded-xl bg-sand-100 border border-sand-300 px-3 py-2 text-xs text-stoneink-700 break-all flex items-center justify-between gap-2">
                <span className="truncate">{url}</span>
                <button onClick={copy} className="shrink-0 text-terracotta-600 hover:text-terracotta-700 flex items-center gap-1" data-testid="qr-copy-btn">
                  <Copy size={14} /> copy
                </button>
              </div>

              <div className="mt-6 flex items-center justify-between rounded-xl border border-sand-300 px-4 py-3">
                <div>
                  <div className="text-sm font-medium text-stoneink-900 flex items-center gap-2">
                    {isPublic ? <><Eye size={16} className="text-sage-600" /> Public</> : <><EyeSlash size={16} className="text-stoneink-500" /> Private</>}
                  </div>
                  <div className="text-xs text-stoneink-500 mt-0.5">
                    {isPublic ? "Anyone with the link sees contents." : "Only signed-in owner/family can view."}
                  </div>
                </div>
                <button onClick={togglePublic} data-testid="qr-toggle-public" className={`h-9 px-4 rounded-xl text-sm font-medium transition-colors ${isPublic ? "bg-sand-200 hover:bg-sand-300 text-stoneink-900" : "bg-terracotta-500 hover:bg-terracotta-600 text-white"}`}>
                  {isPublic ? "Make private" : "Make public"}
                </button>
              </div>

              <div className="mt-6 flex gap-3">
                <button onClick={print} className="flex-1 h-11 rounded-xl bg-stoneink-900 hover:bg-stoneink-700 text-white font-medium" data-testid="qr-print-btn">
                  Print label
                </button>
                <a href={url} target="_blank" rel="noreferrer" className="flex-1 h-11 rounded-xl border border-sand-300 hover:border-ochre-500 text-stoneink-900 font-medium grid place-items-center">
                  Open
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
